/**
 * The 44px product thumbnail on an article row — audit v6 V6-09, `docs/adr/9013`.
 *
 * ## Why a thumbnail is allowed here when ADR 6001 refused one in the grid
 *
 * ADR 6001 rejected a thumbnail column in `PlanningGrid` on two grounds: `bench:grid`'s 100k
 * rows at 60fps, and one presigned read per row. Neither holds for a `DataTable`:
 *
 *   * it draws at most fifty rows (ADR 9008) — the budget is the DOM of fifty `<img>`s, not
 *     of a virtualised hundred thousand;
 *   * the platform publishes a BATCH read for exactly this — `GET /product-images/renditions`,
 *     "the batch the virtualised grid resolves for the rows a planner can see", up to 200
 *     article keys per request — so fifty rows are ONE request, not fifty. (The designer's
 *     `GET /product-images/{article}` is the per-article DETAIL read, with every rendition and
 *     the provenance; it stays on the drill target, as ADR 6001 put it.)
 *
 * `PlanningGrid` is untouched: its budget is the one ADR 6001 defended.
 *
 * ## The width
 *
 * 44 CSS px is 88 device px on a 2× display, so the 96px rendition — the smallest the platform
 * signs — is asked for with `width=96`, and a set without a 96 rendition gets the placeholder:
 * a width absent from `availableWidths` was absent at the source and is never substituted
 * (`widestAtMost`'s rule, the platform's rule).
 *
 * ## Presence is not a measure (leader ruling 7, bata-data ADR 0214, ADR 0098)
 *
 * An image exists because the article is listed on the consumer storefront. So this component
 * SHOWS the image and nothing else reads it: the lookup is keyed by article and hands back a URL
 * or nothing, there is no `hasImage` anywhere, and the `DataTable` column it lives in has no
 * `value` — it cannot be sorted, filtered or exported, by construction rather than by
 * discipline.
 *
 * ## Absence, failure and an expired signature look the same, and that is deliberate here
 *
 * On a detail header `ProductImage` distinguishes three absences, because one article is the
 * subject. On a row the article code beside the box is the subject, and fifty differently-worded
 * absences would be the "broken table" ADR 6001 warned about. So every non-image outcome — the
 * article has none, no index is promoted (503), the batch failed, the 300-second signature
 * expired and the `<img>` errored — is the same quiet box of the same size. No broken-image
 * glyph, and no layout jump when the image arrives or fails.
 */

import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useApiQuery } from '../../api';
import { readRenditions, type ProductImageSets } from '../../api/generated';

/** The drawn size, CSS px. */
export const THUMBNAIL_SIZE = 44;
/** The rendition asked for: the smallest signed width covering 44px at 2×. */
export const THUMBNAIL_WIDTH = 96;
/** The batch route's ceiling (the spec: "at most 200 article keys"). */
export const THUMBNAIL_BATCH_LIMIT = 200;

/** Article key → the signed URL of its 96px rendition. A key with no entry has no image to show. */
export type ThumbnailLookup = ReadonlyMap<string, { readonly url: string; readonly width: number; readonly height: number }>;

/**
 * The URLs a batch answer carries, by article. Only a `published` set with a 96px rendition
 * yields an entry; everything else yields none. Pure — the tests call it.
 */
export function thumbnailLookup(sets: ProductImageSets | undefined): ThumbnailLookup {
  const out = new Map<string, { url: string; width: number; height: number }>();
  for (const set of sets?.images ?? []) {
    if (set.status !== 'published') continue;
    const rendition = set.renditions.find((r) => r.width === THUMBNAIL_WIDTH);
    if (rendition !== undefined) out.set(set.articleKey, { url: rendition.url, width: rendition.width, height: rendition.height });
  }
  return out;
}

/**
 * One batched read for the article keys a table DRAWS. Keys are de-duplicated and sorted for
 * the cache key, so a re-sort of the same fifty rows is the same request. Above the route's
 * ceiling the rest get no image rather than a second request: a table never draws that many.
 */
export function useArticleThumbnails(articleKeys: readonly string[]): ThumbnailLookup {
  const keys = useMemo(
    () => [...new Set(articleKeys.filter((key) => key !== ''))].sort().slice(0, THUMBNAIL_BATCH_LIMIT),
    [articleKeys],
  );
  const query = useApiQuery(
    readRenditions,
    { query: { articleKey: keys, width: THUMBNAIL_WIDTH } },
    { enabled: keys.length > 0 },
  );
  return useMemo(() => thumbnailLookup(query.data), [query.data]);
}

const ThumbnailContext = createContext<ThumbnailLookup | null>(null);

/**
 * Resolves the thumbnails for `articleKeys` once and hands them to every `ArticleThumbnail`
 * inside. `DataTable`'s `thumbnail` prop mounts it over the rows it draws; a table that draws
 * its own body mounts it itself.
 */
export function ArticleThumbnailProvider({ articleKeys, children }: {
  readonly articleKeys: readonly string[];
  readonly children: ReactNode;
}): React.JSX.Element {
  const lookup = useArticleThumbnails(articleKeys);
  return <ThumbnailContext.Provider value={lookup}>{children}</ThumbnailContext.Provider>;
}

export interface ArticleThumbnailProps {
  /** `ARV_ARTICLE` verbatim. */
  readonly articleKey: string;
  /** Ghost mode: the box alone. */
  readonly ghost?: boolean;
}

/**
 * The 44px box, and the image in it when there is one. Decorative (`alt=""`): the article code
 * is the row's own text beside it (ADR 0098 decision 5's precondition, which a row satisfies).
 */
export function ArticleThumbnail({ articleKey, ghost = false }: ArticleThumbnailProps): React.JSX.Element {
  const lookup = useContext(ThumbnailContext);
  const [failed, setFailed] = useState<string | null>(null);
  const image = ghost ? undefined : lookup?.get(articleKey);
  const url = image !== undefined && failed !== image.url ? image.url : null;
  return (
    <span
      data-thumbnail={url === null ? 'none' : 'image'}
      className={`${ghost ? 'bg-ghost' : 'bg-surface-sunken'} border-hairline block shrink-0 overflow-hidden rounded-chip border`}
      style={{ inlineSize: THUMBNAIL_SIZE, blockSize: THUMBNAIL_SIZE }}
    >
      {url !== null && (
        <img
          src={url}
          alt=""
          width={THUMBNAIL_SIZE}
          height={THUMBNAIL_SIZE}
          loading="lazy"
          decoding="async"
          // An expired signature or a missing object 403s/404s; the box stays, the glyph never shows.
          onError={() => setFailed(url)}
          className="block size-full object-cover"
        />
      )}
    </span>
  );
}
